import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack, Button, TextField, Typography, Alert } from '@mui/material';
import { Sheet } from '../../components';
import useFetch from '../../hooks/useFetch';
import { formatCents, parseMoney } from './money';
import { formatDay } from './dates';

const duplicateCheckNumber = error => /^check number (\d+) is already on the calendar/.exec(error || '');

export default function CheckSheet({ item, perms, setAuth, onClose, onSaved }) {
  const { t, i18n } = useTranslation();
  const { fetchResource, isPending } = useFetch({ setAuth });
  const [action, setAction] = useState(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const waiting = item.status === 'scheduled';
  const title = item.kind === 'check' ? `${item.payee} #${item.check_number}` : item.payee;

  const open = (a, initial = '') => { setAction(a); setValue(initial); setError(''); };
  const call = async (path, body) => {
    const r = await fetchResource({ resource: `calendar/outflows/${item.id}/${path}`, body, loadingKey: 'sheet' });
    if (r.error) {
      const dup = duplicateCheckNumber(r.error);
      if (dup) return setError(t('calendar.checks.duplicate', { number: dup[1] }));
      return setError(r.error === 'no longer waiting' ? t(`calendar.${item.kind}.noLongerWaiting`) : r.error);
    }
    onSaved();
  };
  const submit = () => {
    if (action === 'date') return call('edit', { dueDate: value });
    if (action === 'amount') return call('edit', { amountCents: parseMoney(value) });
    if (action === 'number') return call('edit', { checkNumber: value });
    if (action === 'cancel') return call('cancel', { reason: value.trim() });
  };
  const valid = action === 'cancel' ? value.trim().length > 0 : action === 'amount' ? parseMoney(value) != null : Boolean(value);

  return (
    <Sheet open onClose={onClose} title={title} size='xs'>
      <Stack spacing={2}>
        <Typography>{formatCents(item.amount_cents)} · {formatDay(item.due_date, i18n.language)} · {t(`calendar.states.${item.status}`)}</Typography>
        {error && <Alert severity='warning'>{error}</Alert>}
        {!action && item.vendor_id && <Button component={Link} to={`/calendar-vendor/${item.vendor_id}`}>{item.payee}</Button>}
        {!action && waiting && perms.CAL_ENTER && (
          <>
            <Button onClick={() => open('date', item.due_date)}>{t('calendar.check.changeDate')}</Button>
            <Button onClick={() => open('amount', (item.amount_cents / 100).toFixed(2))}>{t('calendar.check.changeAmount')}</Button>
            {item.kind === 'check' && <Button onClick={() => open('number', item.check_number)}>{t('calendar.check.changeNumber')}</Button>}
            <Button color='warning' onClick={() => open('cancel')}>{t(`calendar.${item.kind}.cancel`)}</Button>
          </>
        )}
        {!action && perms.CAL_MANAGE && (
          <>
            {item.status === 'void' && <Button disabled={isPending('sheet')} onClick={() => call('uncancel')}>{t('calendar.check.uncancel')}</Button>}
            {item.status === 'probably_cleared' && (
              <>
                <Button disabled={isPending('sheet')} onClick={() => call('confirm-probable')}>{t('calendar.admin.confirm')}</Button>
                <Button disabled={isPending('sheet')} onClick={() => call('reject-probable')}>{t('calendar.admin.reject')}</Button>
              </>
            )}
          </>
        )}
        {action && (
          <>
            <TextField autoFocus label={t(action === 'cancel' ? 'calendar.check.reason' : `calendar.checks.${action === 'date' ? 'date' : action === 'amount' ? 'amount' : 'number'}`)}
              type={action === 'date' ? 'date' : 'text'} InputLabelProps={{ shrink: true }} value={value} onChange={e => setValue(e.target.value)} />
            <Stack direction='row' spacing={1}>
              <Button onClick={() => setAction(null)}>{t('common.back')}</Button>
              <Button variant='contained' disabled={!valid || isPending('sheet')} onClick={submit}>{t('calendar.review.save')}</Button>
            </Stack>
          </>
        )}
      </Stack>
    </Sheet>
  );
}
